import React from 'react';
import { CheckCircle, AlertTriangle, Lock, Loader2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

/**
 * Capacity indicator badge that shows availability status
 * Displays next to date picker fields
 */
export function CapacityIndicator({ 
  status, 
  availableSlots, 
  totalSlots,
  marketName,
  weekStartDate,
  className = '' 
}) {
  // Don't render if status is idle or no data
  if (status === 'idle' || availableSlots === null) {
    return null;
  }
  
  // Format week date for tooltip
  const formatWeekDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  // Get styling based on status
  const getStatusConfig = () => {
    switch (status) {
      case 'loading':
        return {
          icon: Loader2,
          iconClass: 'animate-spin',
          bgClass: 'bg-slate-500/20',
          textClass: 'text-slate-500',
          label: 'Checking...'
        };
      case 'available':
        return {
          icon: CheckCircle,
          iconClass: '',
          bgClass: 'bg-emerald-500/20',
          textClass: 'text-emerald-600 dark:text-emerald-400',
          label: `${availableSlots} slots available`
        };
      case 'warning':
        return {
          icon: AlertTriangle,
          iconClass: '',
          bgClass: 'bg-amber-500/20',
          textClass: 'text-amber-600 dark:text-amber-400',
          label: `${availableSlots} slot${availableSlots !== 1 ? 's' : ''} left (Tight)`
        };
      case 'full':
        return {
          icon: Lock,
          iconClass: '',
          bgClass: 'bg-red-500/20',
          textClass: 'text-red-600 dark:text-red-400',
          label: 'Week is full'
        };
      case 'error':
        return {
          icon: AlertTriangle,
          iconClass: '',
          bgClass: 'bg-slate-500/20',
          textClass: 'text-slate-500',
          label: 'Unable to check'
        };
      default:
        return null;
    }
  };
  
  const config = getStatusConfig();
  if (!config) return null;
  
  const Icon = config.icon;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline"
            className={`
              flex items-center gap-1.5 px-2 py-1 cursor-help
              ${config.bgClass} ${config.textClass} border-transparent
              ${className}
            `}
            data-testid="capacity-indicator"
          >
            <Icon className={`w-3.5 h-3.5 ${config.iconClass}`} />
            <span className="text-xs font-medium">{config.label}</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="font-medium">Week of {formatWeekDate(weekStartDate)}</p>
          {marketName && <p className="text-xs text-muted-foreground">{marketName}</p>}
          <p className="text-xs mt-1">
            {availableSlots} of {totalSlots} slots available
          </p>
          {status === 'full' && (
            <p className="text-xs text-amber-500 mt-1">
              Sales cannot book into full weeks. Contact PM for override.
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default CapacityIndicator;
